import "./MerchantInscription.scss";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";

import saveOrgAndUserDataTransaction from "../../../../utility/saveOrgAndUserDataTransaction";
import useFirestoreErrorTranslation from "../../../../utility/useFirestoreErrorTranslation";
import useMerchantInscriptionValidationSchema from "../../../../components/validationsSchemas/useMerchantInscriptionValidationSchema";
import createStripeAccount from "../../../../utility/createStripeAccount";
import ModalComponent from "../../../../components/modal/Modal";
import { Loader2 } from "../../../../helper/loader";
import FormField from "../../../../components/formField/FormField";
import LogoImage from "../../../../components/logos/logo.svg";
import { db } from "../../../../firebase";
import Dropdown from "../../../../components/Dropdown";

function MerchantInscription({ org, takeaway }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    isCompany: false,
    businessCategory: "",
    businessStreet: "",
    businessStreetNumber: "",
    businessCity: "",
    businessPostalCode: "",
    paymentDescriptor: "",
    vatNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation("merchantInscription");
  const [errorMessage, setErrorMessage] = useState("");
  const userStore = useSelector((state) => state.userReducer);
  const orgId = org?.id;
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgToCopy, setSelectedOrgToCopy] = useState("");

  const [modalInformations, setModalInformations] = useState({
    content: "",
    title: "",
  });

  const errorTranslation = useFirestoreErrorTranslation();
  const ACTIVITY_OPTIONS = [
    { id: "1", label: t("sportClub"), value: "Club de sport" },
    { id: "2", label: t("youthCommittee"), value: "Comité de Jeunesse" },
    { id: "3", label: t("schoolCommittee"), value: "Comité scolaire" },
    { id: "4", label: t("festivalCommittee"), value: "Comité des fêtes" },
    { id: "5", label: t("folklore"), value: "Folklore" },
    { id: "6", label: t("nonProfitOrganization"), value: "ASBL" },
    { id: "7", label: t("eventCompany"), value: "Entreprise" },
    { id: "8", label: t("other"), value: "Autre" },
  ];
  const YES_NO_OPTIONS = [
    { id: "yes", label: t("option_yes"), value: true },
    { id: "no", label: t("option_no"), value: false },
  ];
  const BUSINESS_CATEGORY_OPTIONS = [
    { id: "events", label: t("business_events"), value: "7922" },
    { id: "gatherings", label: t("business_gatherings"), value: "7999" },
    { id: "tourism", label: t("business_tourism"), value: "7991" },
    { id: "hotels", label: t("business_hotels"), value: "7011" },
    { id: "concert", label: t("business_concert"), value: "7922" },
    { id: "stadium", label: t("business_stadium"), value: "7941" },
    { id: "nonProfit", label: t("business_nonprofit"), value: "8641" },
    { id: "youthClubs", label: t("business_youth_clubs"), value: "7032" },
  ];
  const merchantInscriptionValidationSchema =
    useMerchantInscriptionValidationSchema(ACTIVITY_OPTIONS);

  useEffect(() => {
    if (!orgId || Object.keys(org).length <= 0 || org.stripeActivated) {
      navigate(-1);
    } else {
      handleChange("email", userStore.users.email);
      handleChange("postalCode", userStore.users.postalCode);
      handleChange("organizationName", org.organization);
      handleChange("activitySector", org.activity);
      fetchOrganizations();
    }
  }, [orgId, navigate]);

  const fetchOrganizations = async () => {
    if (!userStore.users?.uid) return;

    try {
      const q = query(
        collection(db, "Organizations"),
        where("adminUid", "array-contains", userStore.users.uid),
        where("stripeActivated", "==", true),
      );
      const querySnapshot = await getDocs(q);
      const orgs = [];
      querySnapshot.forEach((doc) => {
        orgs.push(doc.data());
      });

      const orgsWithStripe = orgs.filter((o) => o.id !== orgId && o.stripeId && o.stripeActivated);
      setOrganizations(orgsWithStripe);

      if (orgsWithStripe.length > 0) {
        setSelectedOrgToCopy(orgsWithStripe[0].id);
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  async function fieldsVerification() {
    try {
      await merchantInscriptionValidationSchema.validate(formData, {
        abortEarly: false,
      });
      setErrors({});
      saveForm();
    } catch (err) {
      console.error(err);
      const formattedErrors = {};
      err.inner.forEach((error) => {
        formattedErrors[error.path] = t(error.message);
      });
      setErrors(formattedErrors);
    }
  }

  function handleChange(name, value) {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  }

  const handleCopyStripeId = async () => {
    if (!selectedOrgToCopy) return;

    const selectedOrg = organizations.find((o) => o.id === selectedOrgToCopy);
    if (!selectedOrg?.stripeId) return;

    setIsLoading(true);
    try {
      await saveOrgAndUserDataTransaction(
        orgId,
        userStore.users.uid,
        {
          stripeId: selectedOrg.stripeId,
          stripeActivated: true,
          bankAccount: selectedOrg.bankAccount,
          businessAddress: selectedOrg.businessAddress,
          isCompany: selectedOrg.isCompany,
          legalName: selectedOrg.legalName,
          vatNumber: selectedOrg.vatNumber,
        },
        {},
      );

      setModalInformations({
        title: t("success", "Success"),
        content: t(
          "stripe_account_copied",
          "Stripe account successfully copied! You can now start selling tickets.",
        ),
      });
    } catch (error) {
      setModalInformations({
        title: t("error_stripe_account"),
        content: errorTranslation(error),
      });
      setErrorMessage(error.toString());
      console.error("Error copying Stripe ID:", error);
    }
    setIsLoading(false);
  };

  async function saveForm() {
    setIsLoading(true);
    try {
      const form = {
        Firstname: formData.firstName,
        Name: formData.lastName,
        BirthDate: new Date(formData.birthDate).getTime(),
        PhoneNumber: formData.phoneNumber.trim(),
        Email: formData.email,
        Street: formData.street,
        StreetNumber: formData.streetNumber,
        City: formData.city,
        PostalCode: formData.postalCode,
        Organization: formData.organizationName,
        Url: formData.websiteLink,
        BankAccount: formData.bankNumber.toUpperCase().replace(/\s/g, ""),
        BusinessCategory: formData.businessCategory,
        IsCompany: formData.isCompany,
        VatNumber: formData.vatNumber,
        BusinessStreet: formData.businessStreet,
        BusinessStreetNumber: formData.businessStreetNumber,
        BusinessCity: formData.businessCity,
        BusinessPostalCode: formData.businessPostalCode,
        PaymentDescriptor: formData.paymentDescriptor,
      };
      await saveOrgAndUserDataTransaction(
        orgId,
        userStore.users.uid,
        {
          bankAccount: form.BankAccount,
          url: form.Url,
          vatNumber: form.VatNumber,
          isCompany: form.IsCompany,
          contactEmail: form.Email,
          businessCategory: form.BusinessCategory,
          legalName: form.Organization,
          businessAddress: form.IsCompany
            ? `${form.BusinessStreet} ${form.BusinessStreetNumber}, ${form.BusinessPostalCode} ${form.BusinessCity}`
            : "",
        },
        {
          address: `${form.Street} ${form.StreetNumber}, ${form.PostalCode} ${form.City}`,
          firstName: form.Firstname,
          lastName: form.Name,
        },
      );

      await createStripeAccount(form, null, orgId);
      navigate(0);
    } catch (error) {
      setModalInformations({
        title: t("error_stripe_account"),
        content: errorTranslation(error),
      });
      setErrorMessage(error.toString());
      console.error(t("error_stripe_account"), error);
    }
    setIsLoading(false);
  }

  return (
    <div className="merchantInscription">
      {isLoading && <Loader2 />}
      <ModalComponent
        isOpen={!!modalInformations.title}
        s
        onClose={() => setModalInformations({ title: "", content: "" })}
        title={modalInformations.title}
        message={modalInformations.content}
      />
      <div className="merchantInscriptionHeader">
        <div>
          <h1>{t("title")}</h1>
          <p>{t("description")}</p>
          <p>{t("transaction_fees")}</p>
          <p>{t("free_registration")}</p>
        </div>
        <div>
          <img src={LogoImage} />
        </div>
      </div>

      {organizations.length > 0 && (
        <div className="merchantInscriptionCopySection">
          <h3>{t("copy_from_existing", "Copy from existing organization")}</h3>
          <p>
            {t(
              "copy_description",
              "If you already have a Stripe account set up with another organization, you can copy it here instead of creating a new one.",
            )}
          </p>
          <div className="copyStripeContainer">
            <div className="copyDropdownWrapper">
              <Dropdown
                label={t("select_organization", "Select organization")}
                options={organizations.map((org) => ({
                  label: org.organization,
                  value: org.id,
                }))}
                value={selectedOrgToCopy}
                onChange={(selectedOption) => setSelectedOrgToCopy(selectedOption.value)}
                placeholder={t("choose_organization", "Choose an organization")}
              />
            </div>
            <button onClick={handleCopyStripeId} className="copyButton" disabled={isLoading}>
              {t("copy", "Copy")}
            </button>
          </div>
        </div>
      )}

      <div className="merchantInscriptionForm">
        <div className="merchantInscriptionFormSection">
          <h2>{t("about_you")}</h2>
          <FormField
            name="firstName"
            value={formData.firstName}
            error={errors.firstName}
            onChange={(value) => handleChange("firstName", value)}
            label={t("label_firstName")}
            type="text"
          />
          <FormField
            name="lastName"
            value={formData.lastName}
            error={errors.lastName}
            onChange={(value) => handleChange("lastName", value)}
            label={t("label_lastName")}
            type="text"
          />
          <FormField
            name="birthDate"
            value={formData.birthDate}
            error={errors.birthDate}
            onChange={(value) => handleChange("birthDate", value)}
            label={t("label_birthDate")}
            type="birthDate"
          />
          <FormField
            name="email"
            value={formData.email}
            error={errors.email}
            onChange={(value) => handleChange("email", value)}
            label={t("label_email")}
            type="email"
          />
          <FormField
            name="phoneNumber"
            value={formData.phoneNumber?.value}
            error={errors.phoneNumber}
            onChange={(value) => handleChange("phoneNumber", value)}
            label={t("label_phoneNumber")}
            type="tel"
          />
          <FormField
            name="street"
            value={formData.street}
            error={errors.street}
            onChange={(value) => handleChange("street", value)}
            label={t("label_street")}
            type="text"
          />
          <FormField
            name="streetNumber"
            value={formData.streetNumber}
            error={errors.streetNumber}
            onChange={(value) => handleChange("streetNumber", value)}
            label={t("label_streetNumber")}
            type="text"
          />
          <FormField
            name="city"
            value={formData.city}
            error={errors.city}
            onChange={(value) => handleChange("city", value)}
            label={t("label_city")}
            type="text"
          />
          <FormField
            name="postalCode"
            value={formData.postalCode}
            error={errors.postalCode}
            onChange={(value) => handleChange("postalCode", value)}
            label={t("label_postalCode")}
            type="text"
          />
        </div>

        <div className="merchantInscriptionFormSection">
          <h2>{t("about_your_organization")}</h2>
          <FormField
            name="organizationName"
            value={formData.organizationName}
            error={errors.organizationName}
            onChange={(value) => handleChange("organizationName", value)}
            label={t("label_organizationName")}
            type="text"
          />
          <FormField
            name="websiteLink"
            value={formData.websiteLink}
            error={errors.websiteLink}
            onChange={(value) => handleChange("websiteLink", value)}
            label={t("label_websiteLink")}
            type="url"
          />
          <FormField
            name="bankNumber"
            value={formData.bankNumber}
            error={errors.bankNumber}
            onChange={(value) => handleChange("bankNumber", value)}
            label={t("label_bankNumber")}
            type="text"
          />
          <FormField
            name="paymentDescriptor"
            value={formData.paymentDescriptor}
            error={errors.paymentDescriptor}
            onChange={(value) => handleChange("paymentDescriptor", value)}
            label={t("label_paymentDescriptor")}
            type="text"
          />
          <FormField
            name="businessCategory"
            value={formData.businessCategory}
            error={errors.businessCategory}
            onChange={(value) => handleChange("businessCategory", value)}
            label={t("label_businessCategory")}
            dropDownValues={BUSINESS_CATEGORY_OPTIONS}
            type="dropdown"
          />
          <FormField
            name="isCompany"
            value={formData.isCompany}
            error={errors.isCompany}
            onChange={(value) => handleChange("isCompany", value)}
            label={t("label_isCompany")}
            dropDownValues={YES_NO_OPTIONS}
            type="dropdown"
          />
          {formData.isCompany && (
            <>
              <FormField
                name="vatNumber"
                value={formData.vatNumber}
                error={errors.vatNumber}
                onChange={(value) => handleChange("vatNumber", value)}
                label={t("label_vatNumber")}
                type="text"
              />
              <FormField
                name="businessStreet"
                value={formData.businessStreet}
                error={errors.businessStreet}
                onChange={(value) => handleChange("businessStreet", value)}
                label={t("label_businessStreet")}
                type="text"
              />
              <FormField
                name="businessStreetNumber"
                value={formData.businessStreetNumber}
                error={errors.businessStreetNumber}
                onChange={(value) => handleChange("businessStreetNumber", value)}
                label={t("label_businessStreetNumber")}
                type="text"
              />
              <FormField
                name="businessCity"
                value={formData.businessCity}
                error={errors.businessCity}
                onChange={(value) => handleChange("businessCity", value)}
                label={t("label_businessCity")}
                type="text"
              />
              <FormField
                name="businessPostalCode"
                value={formData.businessPostalCode}
                error={errors.businessPostalCode}
                onChange={(value) => handleChange("businessPostalCode", value)}
                label={t("label_businessPostalCode")}
                type="text"
              />
            </>
          )}
        </div>
      </div>
      <div className="merchantInscriptionButton">
        {errorMessage !== "" && <p className="generalErrorMessage">{errorMessage}</p>}

        <p>{t("confirm_info")}</p>
        <p>{t("responsibility")}</p>

        <button onClick={fieldsVerification}>{t("button_register")}</button>
      </div>
    </div>
  );
}

export default MerchantInscription;
